import React from 'react';
import { mediaFeatures } from '../data/mockData';
import { ExternalLink, Calendar, Newspaper } from 'lucide-react';

const NewsMedia = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/old-courthouse-kuching.jpg"
            alt="News & Media"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <h1 className="heading-1 text-[#dc2626] mb-6 max-w-3xl">
            News & Media
          </h1>
          <p className="body-large text-white/90 max-w-3xl">
            Media coverage, public talks, and articles featuring Dark Tourism Malaysia's research and advocacy work.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626]">
              <p className="body-large text-white/90 mb-4">
                Since its founding, Dark Tourism Malaysia has been featured in national media, invited to 
                speak at universities and conferences, and engaged with heritage organizations to raise 
                awareness about Malaysia's often-overlooked historical sites.
              </p>
              <p className="body-medium text-white/80">
                This page documents our media presence and public engagement efforts to promote responsible 
                dark tourism education in Malaysia.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Media Features by Year */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-12">
              <Newspaper className="text-[#dc2626]" size={48} />
              <h2 className="heading-2 text-[#dc2626]">Media Features</h2>
            </div>

            <div className="space-y-12">
              {mediaFeatures.map((yearGroup) => (
                <div key={yearGroup.year}>
                  <div className="flex items-center gap-4 mb-6">
                    <Calendar className="text-[#dc2626]" size={32} />
                    <h3 className="heading-2 text-white">{yearGroup.year}</h3>
                  </div>

                  <div className="space-y-4">
                    {yearGroup.items.map((item, index) => (
                      <div 
                        key={index}
                        className="bg-[#0f0f10] p-6 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-all group"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h4 className="heading-4 text-white mb-2 group-hover:text-[#dc2626] transition-colors">
                              {item.title}
                            </h4>
                            <div className="flex items-center gap-4 text-[#888680]">
                              <span className="body-small font-semibold text-white/90">{item.outlet}</span>
                              <span className="caption">{item.date}</span>
                            </div>
                          </div>
                          <a 
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#dc2626] hover:text-[#dc2626]/80 transition-colors flex-shrink-0"
                          >
                            <ExternalLink size={24} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Public Talks & Appearances */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8">Public Talks & Appearances</h2>
            
            <p className="body-medium text-white/80 mb-12">
              We regularly present at academic conferences, universities, heritage forums, and public 
              events to share research findings and promote thoughtful engagement with dark tourism.
            </p>

            <div className="space-y-6">
              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-white mb-2">Understanding Dark Tourism in Malaysia</h3>
                    <p className="body-small text-[#888680]">Universiti Putra Malaysia - November 2024</p>
                  </div>
                </div>
                <p className="body-small text-white/70">
                  Guest lecture for tourism students exploring how dark tourism is perceived and practiced 
                  in the Malaysian context, including cultural sensitivities and ethical considerations.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-white mb-2">Heritage Preservation Panel Discussion</h3>
                    <p className="body-small text-[#888680]">Penang Heritage Trust - October 2024</p>
                  </div>
                </div>
                <p className="body-small text-white/70">
                  Panel discussion on preserving difficult histories and sites of trauma as part of 
                  Malaysia's national heritage, addressing both conservation challenges and public perception.
                </p>
              </div>

              <div className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222]">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="heading-4 text-white mb-2">Dark Tourism Research Symposium</h3>
                    <p className="body-small text-[#888680]">International Tourism Conference - September 2024</p>
                  </div>
                </div>
                <p className="body-small text-white/70">
                  Presentation of doctoral research findings on the under-documentation of Malaysian dark 
                  tourism sites and recommendations for heritage tourism development.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Media */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="heading-2 text-[#dc2626] mb-8">Media Inquiries</h2>
            
            <p className="body-large text-white/90 mb-8">
              For interviews, speaking engagements, or media collaborations, please get in touch.
            </p>

            <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626] text-left max-w-2xl mx-auto">
              <h3 className="heading-4 text-white mb-4">We can speak about:</h3>
              <ul className="space-y-3 body-medium text-white/80">
                <li>• Dark tourism research and findings in Malaysia</li>
                <li>• Heritage preservation and sites at risk</li>
                <li>• Cultural sensitivities in dark tourism</li>
                <li>• Educational approaches to difficult histories</li>
                <li>• Specific Malaysian dark tourism sites and their significance</li>
              </ul>
            </div>

            <div className="mt-12">
              <a href="/contact" className="btn-primary">
                Contact Us <ExternalLink className="ml-2" size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsMedia;
